<template>
  <BaseCard
    class="report-card"
    :padding="{ mobile: 40, tablet: 40, desktop: 48 }"
    shadow="shadow-grey-small"
    border-radius="20"
  >
    <div class="report-card__content">
      <img
        v-if="report.content.cover_image"
        :src="report.content.cover_image.filename"
        :alt="report.content.title"
        class="report-card__image"
      />
      <div class="report-card__right">
        <div class="report-card__pills">
          <span v-if="report.isNew" class="report-card__new-pill">
            {{ $t('common.footer.new') }}
          </span>
          <span
            v-for="lang in report.content.available_languages"
            :key="lang"
            class="report-card__pill"
          >
            {{ langs[lang as keyof typeof langs] }}
          </span>
          <span
            v-for="tag in report.content.tags"
            :key="tag._uid"
            class="report-card__pill"
          >
            {{ tag.content && tag.content.name }}
          </span>
        </div>
        <h4 class="report-card__title">
          {{ report.content.title }}
        </h4>
        <ul class="report-card__points">
          <li
            v-for="point in report.content.content_points"
            :key="point._uid"
            class="report-card__point"
          >
            {{ point.text }}
          </li>
        </ul>
        <NuxtLink
          v-if="!props.report.content.disable_page_generation"
          :to="cardLink"
          class="report-card__link"
        >
          {{ $t('common.reports.get_the_report') }}
        </NuxtLink>
        <NuxtLink
          v-else-if="props.report.content.disable_page_generation && cachedUrl"
          :to="cachedUrl"
          class="report-card__link"
        >
          {{ props.report.content.cta_text }}
        </NuxtLink>
      </div>
    </div>
  </BaseCard>
</template>

<script lang="ts" setup>
import type { SbPageReportStoryblok } from '@/types'

interface Props {
  report: SbPageReportStoryblok
}

const props = defineProps<Props>()

const { locale, t } = useI18n()
const { cachedUrl } = useStoryblokLink(props.report.content.external_link)

const reportsUrl = computed(() => {
  return `/reports/${props.report.slug}`
})
const cardLink = computed(() => {
  if (locale.value === 'en') {
    return props.report.content.available_languages.includes('en')
      ? reportsUrl.value
      : `de${reportsUrl.value}`
  }

  return props.report.content.available_languages.includes('de')
    ? `/de${reportsUrl.value}`
    : reportsUrl.value
})

const langs = {
  en: t('common.reports.english'),
  de: t('common.reports.german'),
}
</script>

<style lang="scss" scoped>
.report-card {
  width: 100%;

  &__image {
    display: block;
    width: 100%;
    max-width: 125px;
    height: auto;
    margin-bottom: 40px;

    @media (min-width: $breakpoint-md) {
      margin-bottom: 0;
    }
  }

  &__title {
    margin-bottom: 24px;
  }

  &__content {
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    column-gap: 0;

    @media (min-width: $breakpoint-md) {
      column-gap: 40px;
      flex-direction: row;
    }
  }

  &__points {
    margin-bottom: 26px;
    display: flex;
    flex-direction: column;
    row-gap: 6px;
  }

  &__point {
    display: flex;
    column-gap: 12px;
    align-items: flex-start;
    font-size: var(--font-size-paragraph-s);

    &::before {
      flex-shrink: 0;
      display: block;
      content: '';
      position: relative;
      top: 6px;
      left: 0;

      @include icon('@/assets/icons/16-list-tick-cobalt.svg', 16px, 16px);
    }
  }

  &__pills {
    display: flex;
    gap: 12px;
    margin-bottom: 12px;
    align-items: flex-start;
    flex-wrap: wrap;
  }

  &__pill {
    text-transform: uppercase;
    background-color: var(--paper-200);
    font-size: var(--font-size-paragraph-2xs);
    line-height: 16px;
    padding: 4px 12px;
    border-radius: 9px;
    font-weight: 600;
  }

  &__new-pill {
    @extend .report-card__pill;

    background-color: var(--cobalt-500);
    color: white;
  }
}
</style>
